import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { styled, css, useTheme } from 'styled-components';

import Icons from '../../Elements/Icons/index.tsx';
import { Media } from '../../theme/Media.tsx';
import { Tooltip } from '../../V2/Feedback/Tooltip/index.tsx';
import { useTableContext } from './context.tsx';
import type { CellContentAlign } from './types.ts';

const TooltipIcon = styled(Icons.QuestionCircle)`
  margin-left: 3px;
`;

const Icon = styled(Icons.Sort)`
  flex: 0 0 auto;
  margin-left: 5px;
`;

export interface IWrapper {
  $sortable?: boolean;
  $mainCell?: boolean;
  $secondaryCell?: boolean;
  $isCollapsedNav?: boolean;
  $width?: number;
}

export const Wrapper = styled.th<IWrapper>`
  cursor: ${({ $sortable }) => $sortable && 'pointer'};
  user-select: none;
  font-weight: normal;
  ${({ $secondaryCell, $isCollapsedNav }) =>
    $secondaryCell &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 1px;
      }
    `}
  ${({ $mainCell, $isCollapsedNav }) =>
    $mainCell &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 100%;
      }
    `}
  ${({ $mainCell, $secondaryCell, $isCollapsedNav, $width }) =>
    !$mainCell &&
    !$secondaryCell &&
    $width &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: ${`${$width}px`};
      }
    `}
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
  }
`;

export interface IContainer {
  $align?: 'left' | 'center' | 'right';
  $nowrap?: boolean;
  $padding?: string;
  $sortPlaceholder?: boolean;
}

export const Container = styled.div<IContainer>`
  display: flex;
  align-items: center;
  ${({ $align }) =>
    $align === 'left' &&
    css`
      justify-content: flex-start;
    `}
  ${({ $align }) =>
    $align === 'center' &&
    css`
      justify-content: center;
    `}
  ${({ $align }) =>
    $align === 'right' &&
    css`
      justify-content: flex-end;
    `}
  font-size: 12px;
  line-height: 14px;
  color: ${({ theme }) => theme.colors.grey100_1};
  padding: ${({ $padding }) => $padding || '10px 14px'};
  white-space: ${({ $nowrap }) => $nowrap && 'nowrap'};
  padding-right: ${({ $sortPlaceholder }) => $sortPlaceholder && '39px'};
`;

export interface ITh {
  children?: React.ReactNode;
  order?: 'ASC' | 'DESC';
  handleChangeOrder?: () => void;
  type?: 'action';
  basis?: 'secondary' | 'main';
  className?: string;
  colSpan?: number;
  dataTestId?: string;
  align?: CellContentAlign;
  nowrap?: boolean;
  padding?: string;
  tooltip?: string;
  $width?: number;
  sortPlaceholder?: boolean;
}

const Th: React.FC<ITh> = ({
  children,
  order = undefined,
  handleChangeOrder,
  className,
  colSpan,
  dataTestId,
  align = 'left',
  nowrap,
  type,
  basis,
  padding,
  tooltip,
  $width,
  sortPlaceholder,
}) => {
  const theme = useTheme();
  const { isCollapsedNav } = useTableContext();
  const checkMobile = useMediaQuery({ query: isCollapsedNav ? Media.mdMax : Media.lgMax });
  const [isMobile, setIsMobile] = React.useState<boolean>(false);
  React.useEffect(() => {
    setIsMobile(checkMobile);
  }, [checkMobile]);

  if (isMobile && !handleChangeOrder && !(type === 'action')) {
    return null;
  }

  return (
    <Wrapper
      $sortable={!!handleChangeOrder}
      onClick={handleChangeOrder}
      className={className}
      colSpan={colSpan}
      data-testid={dataTestId}
      $mainCell={basis === 'main'}
      $secondaryCell={basis === 'secondary'}
      $isCollapsedNav={isCollapsedNav}
      $width={$width}>
      <Container $align={align} $nowrap={nowrap} $padding={padding} $sortPlaceholder={sortPlaceholder}>
        {children}
        {tooltip && (
          <Tooltip placement='top' tooltip={tooltip}>
            <TooltipIcon size={14} color={theme.colors.link100} />
          </Tooltip>
        )}
        {handleChangeOrder && <Icon sort={order} size={20} />}
      </Container>
    </Wrapper>
  );
};

export default React.memo(Th);
